import { useEffect, useState, useMemo } from 'react';
import { api, downloadPdf, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ROUTES } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Map as MapIcon, Layers, Printer, CalendarClock, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import LaheteHistory from '@/components/LaheteHistory';

const empty = { name: '', print_group: '', active: true, sort_order: 100, in_transport_order: false };

export default function RoutesPage() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  const [routes, setRoutes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState({ date: format(new Date(), 'yyyy-MM-dd'), groups: [] });

  const load = async () => {
    try {
      const [{ data: routesData }, { data: snap }] = await Promise.all([
        api.get('/routes'),
        api.get('/routes/today-snapshot').catch(() => ({ data: null })),
      ]);
      setRoutes(routesData || []);
      if (snap) setSnapshot(snap);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Lataus epäonnistui');
    }
  };
  useEffect(() => { load(); }, []);

  const printGroupForToday = async (groupKey) => {
    try {
      const fname = await downloadPdf(
        `/api/lahete/route.pdf?date=${encodeURIComponent(snapshot.date)}&group_key=${encodeURIComponent(groupKey)}`,
        `Lahetteet_${groupKey.replace(/[^A-Za-z0-9_-]+/g, '_')}_${snapshot.date}.pdf`,
      );
      toast.success(`Lähetteet tulostettu (${fname})`);
      load(); // refresh "Tehty"-counts
    } catch (e) {
      toast.error(`Tulostus ei onnistunut: ${e.message}`);
    }
  };

  // Group routes by print_group for the bulk-print preview pane
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of routes) {
      const key = (r.print_group || '').trim() || r.name;
      if (!m.has(key)) m.set(key, { key, routes: [], customers: 0, isGroup: false });
      const g = m.get(key);
      g.routes.push(r);
      g.customers += r.customer_count || 0;
    }
    for (const g of m.values()) g.isGroup = g.routes.length > 1 || (g.routes[0]?.print_group || '').trim() !== '';
    return Array.from(m.values()).sort((a, b) => a.key.localeCompare(b.key, 'fi'));
  }, [routes]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      print_group: r.print_group || '',
      active: r.active,
      sort_order: r.sort_order,
      in_transport_order: !!r.in_transport_order,
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Reitin nimi puuttuu');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        print_group: form.print_group.trim() || null,
        active: form.active,
        sort_order: Number(form.sort_order) || 100,
        in_transport_order: !!form.in_transport_order,
      };
      if (editing) {
        await api.patch(`/routes/${editing.id}`, payload);
        toast.success('Reitti tallennettu');
      } else {
        await api.post('/routes', payload);
        toast.success('Reitti lisätty');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };
  const remove = async (r) => {
    if (!confirm(`Poistetaanko reitti "${r.name}"?`)) return;
    try {
      await api.delete(`/routes/${r.id}`);
      toast.success('Reitti poistettu');
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Logistiikka</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Reitit</h1>
          <p className="text-sm text-[#6A6D66] mt-1 max-w-2xl">
            Reitit ohjaavat asiakkaita kuljetuserille. <strong>Tulostusryhmä</strong> niputtaa useita alireittejä yhden lähete-tulostuksen alle —
            esim. <em>JM reitti 1, 2, 3</em> kaikki tulostusryhmässä <em>JM</em> tulostuvat yhdellä klikkauksella samaan PDF:ään.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={openNew}
            data-testid={ROUTES.addButton}
            className="h-9 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white"
          >
            <Plus size={16} className="mr-1" />
            Lisää reitti
          </Button>
        )}
      </div>

      {snapshot.groups.length > 0 && (
        <TodaySnapshot snapshot={snapshot} onPrint={printGroupForToday} />
      )}

      <LaheteHistory />

      {groups.length > 0 && (
        <div className="bg-[#F4F6F3] border border-[#E5E6E3] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <Layers size={14} className="text-[#2A4B30]" />
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#2A4B30] font-semibold">
              Lähete-tulostusryhmät
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <div
                key={g.key}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${
                  g.isGroup ? 'bg-[#2A4B30] text-white' : 'bg-white border border-[#E5E6E3] text-[#1C1C1A]'
                }`}
                title={g.routes.map((r) => r.name).join(' · ')}
              >
                <span className="font-semibold">{g.key}</span>
                {g.isGroup && (
                  <span className="text-[10px] opacity-80">{g.routes.length} reittiä</span>
                )}
                <span className="text-[10px] opacity-80">· {g.customers} asiakasta</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F9F9F6] border-b border-[#E5E6E3] text-[#6A6D66]">
            <tr>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-12">#</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Reitti</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-44">Tulostusryhmä</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Asiakkaita</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-36" title="Sisällytä kuljetustilauksen PDF:ään (rahdinkuljettajalle)">Kuljetustilaus</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Aktiivinen</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32"></th>
            </tr>
          </thead>
          <tbody>
            {routes.map((r) => (
              <tr key={r.id} data-testid={`${ROUTES.rowPrefix}${r.id}`} className="border-b border-[#E5E6E3] hover:bg-[#F9F9F6] transition-colors">
                <td className="px-3 py-2 text-xs text-[#6A6D66] font-mono">{r.sort_order}</td>
                <td className="px-3 py-2 font-medium text-[#1C1C1A]">
                  <div className="flex items-center gap-2">
                    <MapIcon size={13} className="text-[#6A6D66]" />
                    {r.name}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {r.print_group ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E8F0E9] text-[#2A4B30]">
                      {r.print_group}
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#A0A39C]">— (oma)</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[#1C1C1A] font-mono text-xs">{r.customer_count}</td>
                <td className="px-3 py-2">
                  {r.in_transport_order ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E8F0E9] text-[#2A4B30]" title="Tämä reitti tulostuu kuljetustilaus-PDF:ään">
                      ✓ Mukana
                    </span>
                  ) : (
                    <span className="text-[11px] text-[#A0A39C]">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs ${r.active ? 'text-[#2A4B30]' : 'text-[#A0A39C]'}`}>
                    {r.active ? 'Käytössä' : 'Pois käytöstä'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(r)}
                        data-testid={`${ROUTES.editPrefix}${r.id}`}
                        className="w-8 h-8 rounded-full hover:bg-[#F4F6F3] text-[#6A6D66] hover:text-[#1C1C1A] flex items-center justify-center transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(r)}
                        data-testid={`${ROUTES.deletePrefix}${r.id}`}
                        className="w-8 h-8 rounded-full hover:bg-[#FBEAE5] text-[#6A6D66] hover:text-[#C05A3C] flex items-center justify-center transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {routes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-[#6A6D66]">Ei reittejä</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Muokkaa reittiä' : 'Lisää reitti'}</DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Reitin nimi näkyy asiakaskorteilla. Tulostusryhmä niputtaa alireitit (esim. <em>JM reitti 1, 2, 3 → JM</em>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Reitin nimi</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={ROUTES.dialogName}
                placeholder="esim. JM reitti 1"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Tulostusryhmä (valinnainen)</Label>
              <Input
                value={form.print_group}
                onChange={(e) => setForm({ ...form, print_group: e.target.value })}
                data-testid={ROUTES.dialogPrintGroup}
                placeholder="esim. JM (jätä tyhjäksi jos reitti tulostuu omana)"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Järjestys</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                data-testid={ROUTES.dialogSort}
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm">Aktiivinen</Label>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid={ROUTES.dialogActive}
              />
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-[#E5E6E3] mt-2 pt-3">
              <div>
                <Label className="text-sm">Sisällytä kuljetustilaukseen</Label>
                <p className="text-[11px] text-[#6A6D66] mt-0.5">Tämä reitti tulostetaan rahdinkuljettajalle lähetettävään Kuljetustilaus-PDF:ään.</p>
              </div>
              <Switch
                checked={!!form.in_transport_order}
                onCheckedChange={(v) => setForm({ ...form, in_transport_order: v })}
                data-testid="routes-dialog-in-transport-order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Peruuta</Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid={ROUTES.dialogSave}
              className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            >
              {saving ? 'Tallennetaan…' : 'Tallenna'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TodaySnapshot({ snapshot, onPrint }) {
  const date = snapshot.date;
  const niceDate = (() => {
    try {
      const d = new Date(date + 'T00:00:00');
      return d.toLocaleDateString('fi-FI', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' });
    } catch { return date; }
  })();
  const totalKg = snapshot.groups.reduce((a, g) => a + g.total_kg, 0);
  const totalLah = snapshot.groups.reduce((a, g) => a + g.lahete_count, 0);
  return (
    <div className="bg-white border border-[#E5E6E3] rounded-2xl shadow-sm overflow-hidden">
      <div className="px-5 py-3 bg-gradient-to-br from-[#2A4B30] to-[#1E3622] text-white flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <CalendarClock size={16} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] opacity-80 font-semibold">Tämän päivän tilaukset</p>
            <p className="text-sm font-heading capitalize">{niceDate}</p>
          </div>
        </div>
        <div className="text-xs flex items-center gap-3 font-mono opacity-90">
          <span>{totalLah} lähetettä</span>
          <span>· {Math.round(totalKg)} kg</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4">
        {snapshot.groups.map((g) => {
          const allDone = g.lahete_done === g.lahete_count;
          return (
            <div
              key={g.group_key}
              data-testid={`routes-snapshot-group-${g.group_key}`}
              className={`rounded-xl border p-3 flex flex-col gap-2 transition-colors ${
                allDone ? 'bg-[#F0F6F0] border-[#CFE0D2]' : 'bg-[#FAFAF8] border-[#E5E6E3]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    g.is_group ? 'bg-[#2A4B30] text-white' : 'bg-white border border-[#E5E6E3] text-[#1C1C1A]'
                  }`}>
                    {g.group_key}
                  </span>
                  {g.is_group && g.sub_routes.length > 1 && (
                    <span className="text-[10px] text-[#6A6D66]">{g.sub_routes.length} alireittiä</span>
                  )}
                </div>
                {allDone && <CheckCircle2 size={14} className="text-[#2A4B30]" />}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-2xl text-[#1C1C1A] tabular-nums">{g.lahete_count}</span>
                <span className="text-xs text-[#6A6D66]">lähetettä</span>
                <span className="text-xs text-[#6A6D66] ml-auto font-mono">{Math.round(g.total_kg)} kg</span>
              </div>
              <div className="text-[11px] text-[#6A6D66]">
                {g.lahete_done > 0 && (
                  <span className="text-[#2A4B30] font-medium">
                    {g.lahete_done}/{g.lahete_count} tehty
                  </span>
                )}
                {g.lahete_done === 0 && <span>Ei vielä tulostettuja</span>}
              </div>
              <button
                type="button"
                onClick={() => onPrint(g.group_key)}
                data-testid={`routes-snapshot-print-${g.group_key}`}
                className="mt-auto h-8 px-3 rounded-full text-xs font-medium flex items-center justify-center gap-1.5 bg-[#2A4B30] hover:bg-[#1E3622] text-white transition-colors"
                title={`Tulosta kaikki ${g.group_key} -reitin lähetteet`}
              >
                <Printer size={12} />
                Tulosta kaikki
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
